
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, Typography, Alert, Space, Spin, Result, Divider } from 'antd';
import { CheckCircleOutlined, WarningOutlined, CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { downloadPPIReport } from '../../../services/getPPI';
import { authPpiApiAxiosWithRefresh } from '../../../services/api';
const { Title, Paragraph } = Typography;

interface Suggestion {
  id: string;
  severity: 'high' | 'medium' | 'low' | 'success';
  title: string;
  message: string;
}

interface NextStepsProps {
  docId: string;
  email?: string | null;
  emailError?: string | null;
  sendEmail: boolean;
  onComplete?: () => void;
  onBack?: () => void;
}

const NextSteps: React.FC<NextStepsProps> = ({ docId, email, emailError, sendEmail, onComplete, onBack }) => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    const fetchNextSteps = async () => {
      try {
        setLoading(true);
        const response = await (await authPpiApiAxiosWithRefresh()).get(`/next-steps-analysis/${docId}`);
        const data = await response.data;
        if (data.success) {
          setSuggestions(data.suggestions);
        } else {
          throw new Error(data.error || 'Unknown error occurred');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchNextSteps();
  }, [docId]);

  const getIcon = (severity: Suggestion['severity']) => {
    switch (severity) {
      case 'high':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'medium':
        return <WarningOutlined style={{ color: '#faad14' }} />;
      case 'low':
        return <WarningOutlined style={{ color: '#faad14' }} />;
      case 'success':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      default:
        return null;
    }
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const blob = await downloadPPIReport(docId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `PPI_Report_${docId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download PDF:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <Spin size="large" />
        <Title level={4} style={{ marginTop: 16 }}>Analyzing for Future Improvements...</Title>
      </div>
    );
  }

  if (error) {
    return <Alert message="Error" description={error} type="error" showIcon />;
  }

  return (
    <div style={{ padding: '20px' }}>
      {emailError ? (
        <div style={{ textAlign: 'center' }}>
          <WarningOutlined style={{ fontSize: 48, color: '#faad14', marginBottom: 16 }} />
          <Title level={3}>Report Generated with an Issue</Title>
          <Paragraph type="secondary" style={{ fontSize: 16 }}>
            The analysis is complete and your report has been saved to your profile.
          </Paragraph>
          <Alert
            message="Email Delivery Failed"
            description={emailError}
            type="warning"
            showIcon
            style={{ textAlign: 'left', marginBottom: 24 }}
          />
        </div>
      ) : sendEmail ? (
        <Result
          status="success"
          title="Report Sent!"
          subTitle={`A PDF of the report has been sent to ${email}`}
        />
      ) : (
        <Result
          status="success"
          title="Report Generated!"
          subTitle="Result completed, you can download a copy of the report below."
        />
      )}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16, marginBottom: 24 }}>
        <Button onClick={handleDownload} disabled={isDownloading}>
          {isDownloading ? <><LoadingOutlined spin /> Downloading...</> : 'Download Report'}
        </Button>
      </div>
      <Divider />
      <Title level={4} style={{ textAlign: 'center', marginBottom: 16 }}>For an even better analysis next time, consider rerunning the report with the following suggestions.</Title>
      <Space direction="vertical" style={{ width: '100%' }}>
        {suggestions.map(suggestion => (
          <Card key={suggestion.id} bordered={false} style={{ background: '#f9f9f9' }}>
            <Space align="start">
              <span style={{ fontSize: '24px', lineHeight: 1 }}>{getIcon(suggestion.severity)}</span>
              <div>
                <Title level={5}>{suggestion.title}</Title>
                <Paragraph style={{ whiteSpace: 'pre-line' }}>{suggestion.message}</Paragraph>
              </div>
            </Space>
          </Card>
        ))}
      </Space>
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 32 }}>
        <Link to={`/create-report?docId=${docId}`}>
          <Button type="primary" size="large">
            Update Vehicle Details & Rerun
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default NextSteps;
